import { useState } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useVacationTour } from './hooks/useVacationTour';
import { RulesCenter } from './components/RulesCenter';
import type { HelpStep, HelpTourId, RuleTopic } from './vacationHelp.types';

interface VacationHelpProps {
    tourId: HelpTourId;
    steps: HelpStep[];
    rules?: RuleTopic[];
    autoStartTour?: boolean;
    ready?: boolean;
    defaultRuleTopic?: string;
    onTourStart?: () => void;
    onTourEnd?: () => void;
    onDemoChange?: (demo: string | null) => void;
}

export function VacationHelp({
    tourId,
    steps,
    rules = [],
    autoStartTour = false,
    ready = true,
    defaultRuleTopic,
    onTourStart,
    onTourEnd,
    onDemoChange,
}: VacationHelpProps) {
    const { user } = useAuth();
    const [showMenu, setShowMenu] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const { startTour, checkFirstVisit } = useVacationTour(
        tourId,
        steps,
        user?.email ?? undefined,
        ready,
        autoStartTour,
        onTourStart,
        onTourEnd,
        onDemoChange
    );
    const isFirstVisit = checkFirstVisit();

    const handleStartTour = () => {
        setShowMenu(false);
        setShowRules(false);
        startTour();
    };

    const handleOpenRules = () => {
        setShowMenu(false);
        setShowRules(true);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setShowMenu(!showMenu)}
                className={`relative rounded-full p-2 transition-colors ${isFirstVisit && autoStartTour ? 'animate-pulse-raro' : 'hover:bg-gray-100'}`}
                aria-label="Abrir ajuda"
                title="Ajuda"
            >
                <QuestionMarkCircleIcon className="h-6 w-6 text-primary-600" />
            </button>

            {showMenu && (
                <div className="absolute right-0 top-full z-40 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <button
                        onClick={handleStartTour}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 font-montserrat first:rounded-t-lg"
                    >
                        🧭 Tour guiado
                    </button>
                    <button
                        onClick={handleOpenRules}
                        disabled={rules.length === 0}
                        className={`w-full px-4 py-3 text-left text-sm font-medium font-montserrat last:rounded-b-lg transition-colors ${
                            rules.length === 0
                                ? 'text-gray-400 opacity-50 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        📖 Regras de Férias
                    </button>
                </div>
            )}

            <RulesCenter
                isOpen={showRules}
                onClose={() => setShowRules(false)}
                topics={rules}
                defaultTopicId={defaultRuleTopic}
                onTourClick={handleStartTour}
            />
        </div>
    );
}
